/**
 * The AI tags an image comes back with, as the card shows them.
 *
 * The backend generates these **inside the upload request** (`get_image_description_tags`, on the
 * 400×400 thumbnail) and stores them only when the model returned parseable JSON, so on the wire a
 * file either has `{ type: "json", data: { "object": ["keyword", …] } }` or nothing at all. The
 * `type: "string"` shape is handled anyway: the resolution attachment path stores it unguarded, and
 * a parser that throws on a shape it was not expecting is a worse failure than a group with no
 * label.
 *
 * Empty is the **failure** state rather than an absence: an image with no tags is one the model
 * could not read, which is what the card says. Videos are never asked, so nothing calls this for
 * them.
 */
export interface ImageTagGroup {
  /** `PERSON`, `VEHICLE`, `SETTING` — uppercased, as the design has it. Empty for a plain string. */
  label: string;
  /** The keywords, already joined for reading. */
  tags: string;
}

const asTags = (value: unknown): string => {
  if (Array.isArray(value)) {
    return value.filter((tag) => typeof tag === "string").join(", ");
  }
  return typeof value === "string" ? value : "";
};

export const imageTagGroups = (raw: unknown): ImageTagGroup[] => {
  if (!raw || typeof raw !== "object") return [];

  const data = (raw as { data?: unknown }).data;
  if (typeof data === "string") {
    return data.trim() ? [{ label: "", tags: data.trim() }] : [];
  }
  if (!data || typeof data !== "object") return [];

  return Object.entries(data as Record<string, unknown>)
    .map(([label, value]) => ({
      label: label.toUpperCase(),
      tags: asTags(value),
    }))
    .filter((group) => group.tags !== "");
};

/** What the copy button puts on the clipboard, in the format production has always used. */
export const imageTagsAsText = (groups: readonly ImageTagGroup[]): string =>
  groups
    .map((group) =>
      group.label ? `${group.label}: ${group.tags}` : group.tags,
    )
    .join(" -- ");
